import dateFormat from 'dateformat'
import { numberToEuroString } from '../../../../libs/currencies'
import i18n from '../../../../translations/i18n'
import {
  ApartmentConfirmation,
  ApartmentInformation,
  MainOwner,
  AdditionalProperties,
  ClientContactInfo,
  ApartmentClientInformation
} from '../../../../types/apartments'
import { ApartmentConfirmationFormData } from '../../../../types/forms/createApartment'
import {
  WaterFeeBasis,
  RentPeriod,
  SecurityDeposit,
  Condition,
  Balcony,
  BuildingType,
  Parking,
  Heating,
  Dishwasher,
  LaundryMachine,
  Sauna,
  CarHeatingSocket,
  PetsAllowed,
  Storage
} from '../../../../types/propValues/apartments'
import { Showing, ShowingType } from '../../../../types/showings'
import { User, isUserWithContactInfo } from '../../../../types/users'
import {
  BuildingInfoDisplay,
  ContractDisplayProps
} from '../../Overview/utils/dataHandling'

const getGeneralTranslationIfExists = (value: any) =>
  value ? `general.${value}` : false

const getUniqueTranslationIfExists = (value: any, propName: string) =>
  value ? `apartment.${propName}.${value}` : '-'

const formatDate = (value: string | undefined) =>
  value ? dateFormat(value, 'dd.mm.yyyy') : undefined

/**
 * Gets initial values from state, either from login or formdata
 */
const getClientInitialValues = (
  client: ApartmentClientInformation | undefined,
  clientContactInfo: ClientContactInfo | undefined
) =>
  client
    ? {
      ...client,
      mainOwner: {
        ...client.mainOwner,
        name: clientContactInfo?.name || '',
        phoneNumber: clientContactInfo?.phoneNumber || '',
        role: 'roleLandlord',
        ...(client.mainOwner.streetAddress && {
          autocompleteLocation: {
            value: client.mainOwner.googlePlaceId || '',
            label: client.mainOwner.streetAddress || ''
          }
        })
      },
      clientType: client.mainOwner.businessId ? 'business' : 'private',
      hasOtherOwners:
        client.otherOwners &&
          client.otherOwners.length > 0 &&
          client.otherOwners[0]?.name
          ? 'true'
          : 'false'
    }
    : {
      mainOwner: {
        name: clientContactInfo?.name || '',
        phoneNumber: clientContactInfo?.phoneNumber || '',
        role: 'roleLandlord'
      },
      clientType: 'private'
    }

/**
 * Gets apartment information form initial values
 */
const getApartmentInitialValues = (
  apartmentInformation?: ApartmentInformation
) => ({
  ...apartmentInformation,
  energyCertification:
    apartmentInformation?.energyCertification ||
    i18n.t('apartment.list.information.energyCertNotRequired'),
  ...(apartmentInformation?.streetAddress && {
    autocompleteLocation: {
      value: apartmentInformation?.googlePlaceId || '',
      label: apartmentInformation?.streetAddress || ''
    }
  })
})

const initialize = (existingValue: any, newValue: any) =>
  existingValue ? existingValue : newValue

const getApartmentBuildingInfo = (
  confirmation: ApartmentConfirmation,
  information: ApartmentInformation
) => {
  const { price } = confirmation
  const {
    rooms,
    kitchen,
    condition,
    storage,
    balcony,
    buildingType,
    buildYear,
    squareMeters,
    floor,
    floors,
    energyCertification
  } = information
  const buildingInfoForDisplay: BuildingInfoDisplay = {
    price: numberToEuroString(price),
    rooms: `${rooms} + ${kitchen}`,
    squareMeters: `${squareMeters.toFixed(2).toString().replace('.', ',')}m²`,
    floor:
      floor && !floors
        ? `${floor}`
        : floor && floors
          ? `${floor}/${floors}`
          : undefined,
    condition: getUniqueTranslationIfExists(
      condition,
      'condition'
    ) as Condition,
    storage: getUniqueTranslationIfExists(storage, 'storage') as Storage,
    balcony: getUniqueTranslationIfExists(balcony, 'balcony') as Balcony,
    buildingType: getUniqueTranslationIfExists(
      buildingType,
      'buildingType'
    ) as BuildingType,
    energyCertification: energyCertification,
    buildYear
  }
  return buildingInfoForDisplay
}

const getApartmentConfirmationInitialValues = (
  apartmentConfirmationStateData: ApartmentConfirmationFormData
) => {
  const apartmentConfirmation: ApartmentConfirmation = {
    ...apartmentConfirmationStateData
  }
  const {
    securityDeposit,
    rentPeriod,
    availableFromDate,
    availableUntilDate,
    promocode
  } = apartmentConfirmation

  return {
    ...apartmentConfirmationStateData,
    securityDeposit: initialize(securityDeposit, 2),
    rentPeriod: initialize(rentPeriod, 'formerlyValid'),
    announcementType: initialize(
      apartmentConfirmationStateData?.announcementType,
      'regular'
    ),
    isSVAMember: !!promocode,
    availableFromDate: availableFromDate
      ? new Date(availableFromDate)
      : undefined,
    availableUntilDate: availableUntilDate
      ? new Date(availableUntilDate)
      : undefined
  } as any
}

const getApartmentConfirmationForOverview = (
  apartmentConfirmationData: ApartmentConfirmation,
  apartmentInformation: ApartmentInformation
) => {
  const apartmentConfirmation: ApartmentConfirmation = {
    ...apartmentConfirmationData
  }
  const {
    availableFromDate,
    availableUntilDate,
    securityDeposit,
    rentPeriod,
    waterFeeBasis,
    waterFee,
    parkingFee,
    showingType,
    securityDepositAmount,
    requirePriceEstimate
  } = apartmentConfirmation
  const confirmationProps: ContractDisplayProps = {
    ...apartmentConfirmation,
    availableFromDate: formatDate(availableFromDate),
    availableUntilDate: formatDate(availableUntilDate),
    securityDepositAmount: securityDepositAmount
      ? numberToEuroString(securityDepositAmount)
      : undefined,
    waterFee: waterFee ? numberToEuroString(waterFee) : undefined,
    parkingFee: parkingFee ? numberToEuroString(parkingFee) : undefined,
    waterFeeBasis: getUniqueTranslationIfExists(
      waterFeeBasis,
      'waterFeeBasis'
    ) as WaterFeeBasis,
    rentPeriod: getUniqueTranslationIfExists(
      rentPeriod,
      'rentPeriod'
    ) as RentPeriod,
    securityDeposit: getUniqueTranslationIfExists(
      securityDeposit,
      'securityDeposit'
    ) as SecurityDeposit,
    showingType: getUniqueTranslationIfExists(
      showingType,
      'showingType'
    ) as ShowingType,
    requirePriceEstimate: getGeneralTranslationIfExists(
      requirePriceEstimate
    ) as string,
    propertyManager: apartmentInformation?.propertyManager,
    renovations: apartmentInformation?.renovations
  }
  return confirmationProps
}

const getAdditionalPropertiesForOverview = (
  apartmentInfoStateData: ApartmentInformation
) => {
  const apartmentInformation: ApartmentInformation = {
    ...apartmentInfoStateData
  }
  const {
    sauna,
    dishwasher,
    laundryMachine,
    parking,
    carHeatingSocket,
    heating,
    petsAllowed
  } = apartmentInformation
  const additionalProps: AdditionalProperties = {
    // ...apartmentInformation,
    sauna: getUniqueTranslationIfExists(sauna, 'sauna') as Sauna,
    parking: getUniqueTranslationIfExists(parking, 'parking') as Parking,
    carHeatingSocket: getUniqueTranslationIfExists(
      carHeatingSocket,
      'carHeatingSocket'
    ) as CarHeatingSocket,
    dishwasher: getUniqueTranslationIfExists(
      dishwasher,
      'dishwasher'
    ) as Dishwasher,
    laundryMachine: getUniqueTranslationIfExists(
      laundryMachine,
      'laundryMachine'
    ) as LaundryMachine,
    heating: getUniqueTranslationIfExists(heating, 'heating') as Heating,
    petsAllowed: getUniqueTranslationIfExists(
      petsAllowed,
      'petsAllowed'
    ) as PetsAllowed
  }
  return additionalProps
}

const getShowingsInitialValues = (showings: Showing[]) =>
  showings
    ? {
      showings: showings.map(({ type, duration, startDate, endDate }) => ({
        type,
        duration,
        startDate,
        endDate
      }))
    }
    : { showings: [] }

const getOwnerContactInfo = (mainOwner: MainOwner | undefined, user: User) => {
  const ownerContactInfo = {
    ...mainOwner,
    ...(isUserWithContactInfo(user) && {
      name: user.data.name,
      email: user.loginEmail,
      phoneNumber: user.data.phoneNumber,
      language: user.data.language
    }),
    ...(mainOwner?.postalCode &&
      mainOwner?.postalArea && {
      postalArea: `${mainOwner.postalCode} ${mainOwner.postalArea}`
    })
  }
  return ownerContactInfo
}

export {
  getClientInitialValues,
  getApartmentInitialValues,
  getApartmentBuildingInfo,
  getAdditionalPropertiesForOverview,
  getApartmentConfirmationInitialValues,
  getShowingsInitialValues,
  getApartmentConfirmationForOverview,
  getOwnerContactInfo
}
